import { useEffect, type RefObject } from 'react';
import { isStagePresented, STAGE_PRESENTED_ATTR } from '@ghostscreen/player-core';

type PlaybackStageProps = {
  stageRef: RefObject<HTMLDivElement>;
  active: boolean;
  onHasMediaChange?: (hasMedia: boolean) => void;
};

/**
 * Full-bleed media mount point for Html5PlaybackEngine.
 * Always mounted so the runtime can attach on boot.
 *
 * "Has media" follows the engine presentation flag (set on first successful
 * mount, cleared only on intentional blank) — not raw DOM emptiness — so
 * playlist restarts that hold the last frame never flash the waiting overlay.
 */
export function PlaybackStage({ stageRef, active, onHasMediaChange }: PlaybackStageProps) {
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || !onHasMediaChange) return;

    const report = () => {
      onHasMediaChange(isStagePresented(stage));
    };

    report();
    const observer = new MutationObserver(report);
    observer.observe(stage, {
      attributes: true,
      attributeFilter: [STAGE_PRESENTED_ATTR],
      childList: true,
    });
    return () => observer.disconnect();
  }, [stageRef, onHasMediaChange]);

  return (
    <div
      ref={stageRef}
      className={active ? 'playback-stage playback-stage--active' : 'playback-stage'}
      aria-hidden={!active}
    />
  );
}
